// Экран обязательной смены пароля (PWD-4): пароль задал администратор (временный/сброс) — пока
// владелец не задаст свой, сервер закрывает API (PWD-3), а приложение показывает только этот экран.
// Временный пароль, которым только что вошли, подставлен из памяти сессии (не хранится); после
// перезагрузки страницы его спрашивает поле. Проверки на клиенте дублируют серверные (длина ≥ 8,
// новый ≠ временного) — чтобы не гонять заведомо отклонённый запрос; источник истины — сервер.

import { useCallback, useState, type CSSProperties, type FormEvent } from 'react'
import { Button } from '@tele-chat/design-system'
import { useSession } from '../auth/SessionContext.js'
import { strings } from '../i18n/strings.js'
import { forcePasswordErrorText } from '../i18n/apiErrors.js'

/** Минимум длины своего пароля — как серверная политика identity (MIN_PASSWORD_LEN). */
export const NEW_PASSWORD_MIN_LEN = 8

export function ForcePasswordChangeScreen(): JSX.Element {
  const { self, temporaryPassword, completePasswordChange, logout } = useSession()
  const [current, setCurrent] = useState(temporaryPassword ?? '')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const knowsCurrent = temporaryPassword !== null

  const submit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault()
      setError(null)
      if (next.length < NEW_PASSWORD_MIN_LEN) {
        setError(strings.forcePassword.tooShort(NEW_PASSWORD_MIN_LEN))
        return
      }
      if (next !== confirm) {
        setError(strings.forcePassword.mismatch)
        return
      }
      if (next === current) {
        setError(strings.forcePassword.sameAsTemp)
        return
      }
      setBusy(true)
      try {
        await completePasswordChange(current, next)
      } catch (err) {
        setError(forcePasswordErrorText(err))
        setBusy(false)
      }
    },
    [current, next, confirm, completePasswordChange],
  )

  const wrap: CSSProperties = {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflowY: 'auto',
  }
  const card: CSSProperties = {
    width: 'min(92vw, 400px)',
    background: 'var(--color-surface)',
    borderRadius: 'var(--radius-card)',
    boxShadow: 'var(--shadow-card)',
    padding: 'var(--space-5)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-3)',
  }
  const input: CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px var(--space-3)',
    borderRadius: 'var(--radius-control)',
    border: '1px solid var(--color-divider)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-md)',
  }
  const muted: CSSProperties = { color: 'var(--color-text-muted)', margin: 0, fontSize: 'var(--font-sm)' }

  return (
    <div data-screen="force-password" style={wrap}>
      <form style={card} onSubmit={(e) => void submit(e)}>
        <h2 style={{ margin: 0 }}>{strings.forcePassword.title}</h2>
        <p style={muted}>{strings.forcePassword.body}</p>
        {self !== null ? (
          <p data-force-password-email="" style={{ ...muted, color: 'var(--color-text)' }}>
            {self.email}
          </p>
        ) : null}
        {/* скрытое поле логина — менеджер паролей сохранит новый пароль к правильной учётке */}
        <input type="email" name="username" autoComplete="username" value={self?.email ?? ''} readOnly hidden />
        {knowsCurrent ? null : (
          <input
            data-field="current-password"
            type="password"
            style={input}
            placeholder={strings.forcePassword.current}
            autoComplete="current-password"
            inputMode="numeric"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        )}
        <input
          data-field="new-password"
          type="password"
          style={input}
          placeholder={strings.forcePassword.next}
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          required
        />
        <input
          data-field="confirm-password"
          type="password"
          style={input}
          placeholder={strings.forcePassword.confirm}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
        <span style={{ fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>
          {strings.forcePassword.hint(NEW_PASSWORD_MIN_LEN)}
        </span>
        {error !== null ? (
          <div data-force-password-error="" role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-sm)' }}>
            {error}
          </div>
        ) : null}
        <Button type="submit" variant="primary" disabled={busy} data-submit="">
          {strings.forcePassword.submit}
        </Button>
        <Button type="button" variant="ghost" data-force-password-logout="" onClick={() => void logout()}>
          {strings.forcePassword.logout}
        </Button>
      </form>
    </div>
  )
}
